import { useCallback, useEffect, useState } from 'react';
import { Plus } from 'lucide-react';
import Button from '../ui/Button';
import FormModal, { Field, inputClass } from '../fieldWork/FormModal';
import labourService from '../../services/labour';
import { errorMessage } from '../../utils/apiResponse';
import type { CostCentre, LabourSettings } from '../../types/labour';
import { Notice } from './shared';

/**
 * Cost centres (places time goes that are not a planting) and the org's
 * costing settings — currency, on-costs, tax. All the org's own figures: the
 * platform keeps no wage or award data. Editing needs Farm Jobs Admin.
 */
export default function SetupPanel({ canEdit, onSettingsChanged }: { canEdit: boolean; onSettingsChanged: () => void }) {
  const [centres, setCentres] = useState<CostCentre[]>([]);
  const [form, setForm] = useState<LabourSettings | null>(null);
  const [editing, setEditing] = useState<CostCentre | 'new' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const load = useCallback(async () => {
    try {
      const [c, s] = await Promise.all([labourService.getCostCentres(true), labourService.getSettings()]);
      setCentres(c);
      setForm(s.settings);
    } catch (e) {
      setError(errorMessage(e, 'Could not load setup'));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveSettings = async () => {
    if (!form) return;
    setSaved(false);
    try {
      setForm(await labourService.saveSettings({
        currency: form.currency.trim().toUpperCase(),
        on_cost_pct: Number(form.on_cost_pct) || 0,
        tax_name: form.tax_name.trim() || 'Tax',
        tax_rate_pct: Number(form.tax_rate_pct) || 0,
        costs_include_tax: form.costs_include_tax,
      }));
      setSaved(true);
      onSettingsChanged();
    } catch (e) {
      setError(errorMessage(e, 'Could not save settings'));
    }
  };
  const set = <K extends keyof LabourSettings>(k: K, v: LabourSettings[K]) => setForm(f => (f ? { ...f, [k]: v } : f));

  return (
    <div className="space-y-6">
      {error && <Notice tone="error">{error}</Notice>}

      {form && (
        <section className="space-y-3">
          <h3 className="font-semibold text-neutral-900">Costing</h3>
          <p className="text-sm text-neutral-500">Your own figures for budgets and job costing.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-xl">
            <Field label="Currency" htmlFor="ls-cur" hint="Three-letter code, e.g. AUD, NZD.">
              <input id="ls-cur" className={`${inputClass} uppercase`} maxLength={3} value={form.currency} disabled={!canEdit}
                onChange={e => set('currency', e.target.value)} />
            </Field>
            <Field label="On-costs on staff time (%)" htmlFor="ls-oncost" hint="Added to your own staff’s rates in reports. Leave 0 if your rates are already the full cost.">
              <input id="ls-oncost" type="number" inputMode="decimal" min={0} max={100} step="0.1" className={inputClass}
                value={form.on_cost_pct} disabled={!canEdit} onChange={e => set('on_cost_pct', Number(e.target.value))} />
            </Field>
            <Field label="Tax name" htmlFor="ls-taxname">
              <input id="ls-taxname" className={inputClass} maxLength={20} value={form.tax_name} disabled={!canEdit}
                onChange={e => set('tax_name', e.target.value)} />
            </Field>
            <Field label="Tax rate (%)" htmlFor="ls-taxrate" hint="Used to take tax out of rates entered including it.">
              <input id="ls-taxrate" type="number" inputMode="decimal" min={0} max={100} step="0.1" className={inputClass}
                value={form.tax_rate_pct} disabled={!canEdit} onChange={e => set('tax_rate_pct', Number(e.target.value))} />
            </Field>
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={form.costs_include_tax} disabled={!canEdit}
              onChange={e => set('costs_include_tax', e.target.checked)}
              className="rounded border-neutral-300 text-indigo-600 focus:ring-indigo-200" />
            <span className="text-neutral-700">Show cost totals including {form.tax_name || 'tax'}</span>
          </label>
          {canEdit && (
            <div className="flex items-center gap-3">
              <Button size="sm" variant="primary" onClick={saveSettings}>Save</Button>
              {saved && <span className="text-sm text-green-700">Saved</span>}
            </div>
          )}
        </section>
      )}

      <section className="space-y-3">
        <div className="flex items-center justify-between gap-2">
          <div>
            <h3 className="font-semibold text-neutral-900">Cost centres</h3>
            <p className="text-sm text-neutral-500">Where time goes that is not a block: packing shed, workshop, travel, wet-weather standby.</p>
          </div>
          {canEdit && <Button size="sm" variant="primary" onClick={() => setEditing('new')}><Plus className="h-4 w-4" /> New</Button>}
        </div>
        {centres.length === 0 ? (
          <p className="text-sm text-neutral-500">None yet.</p>
        ) : (
          <ul className="divide-y divide-neutral-100 border border-neutral-200 rounded-lg" aria-label="Cost centres">
            {centres.map(c => (
              <li key={c.id}>
                <button type="button" disabled={!canEdit} onClick={() => setEditing(c)} className="w-full text-left px-3 py-2 text-sm flex items-center gap-2 disabled:cursor-default">
                  <span className="font-medium text-neutral-900">{c.name}</span>
                  {c.code && <span className="text-xs font-mono text-neutral-500">{c.code}</span>}
                  {c.farm_name && <span className="text-xs text-neutral-500">{c.farm_name}</span>}
                  {!c.active && <span className="text-xs px-2 py-0.5 rounded-full bg-neutral-200 text-neutral-600">Inactive</span>}
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {editing && (
        <CostCentreModal centre={editing === 'new' ? null : editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      )}
    </div>
  );
}

function CostCentreModal({ centre, onClose, onSaved }: { centre: CostCentre | null; onClose: () => void; onSaved: () => void }) {
  const [name, setName] = useState(centre?.name ?? '');
  const [code, setCode] = useState(centre?.code ?? '');
  const [active, setActive] = useState(centre?.active ?? true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setSaving(true);
    try {
      await labourService.saveCostCentre(centre?.id ?? null, { name: name.trim(), code: code || null, ...(centre ? { active } : {}) });
      onSaved();
    } catch (e) {
      setError(errorMessage(e, 'Could not save'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <FormModal title={centre ? 'Edit cost centre' : 'New cost centre'} onClose={onClose} onSubmit={submit}
      submitting={saving} submitDisabled={!name.trim()} error={error}>
      <Field label="Name" htmlFor="cc-name">
        <input id="cc-name" className={inputClass} value={name} onChange={e => setName(e.target.value)} autoFocus />
      </Field>
      <Field label="Code (optional)" htmlFor="cc-code" hint="Your accounting code, if you use one.">
        <input id="cc-code" className={inputClass} value={code} onChange={e => setCode(e.target.value)} />
      </Field>
      {centre && (
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={active} onChange={e => setActive(e.target.checked)}
            className="rounded border-neutral-300 text-indigo-600 focus:ring-indigo-200" />
          <span className="text-neutral-700">Active</span>
        </label>
      )}
    </FormModal>
  );
}
